<script setup lang="ts">
import BaseModal from '@/components/modal/BaseModal.vue';
import { COLOR_CLASSES } from '@/constants/colors';
import { ref } from 'vue';
import ModalFooterConfirmButtons from '@/components/modal/ModalFooterConfirmButtons.vue';
import { MeditationEditModalProps, useModalStore } from '@/stores/modal';
import InputRow from '@/components/TextField/inputRow.vue';
import { handleEditMeditationClickParams } from '@/types/meditation';

interface Props {
  mode: 'create' | 'edit';
  weeklyMeditations: {
    previousWeek: {
      id: number;
      title: string;
      video_url: string;
      date: string;
      is_current: boolean;
      created_at: string;
    };
    currentWeek: {
      id: number;
      title: string;
      video_url: string;
      date: string;
      is_current: boolean;
      created_at: string;
    };
  };
  handleEditMeditationClick: (params: handleEditMeditationClickParams) => void;
}

interface Emits {
  (e: 'confirm'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const isSubmitting = ref(false);

const modalStore = useModalStore();

const previousWeek = ref({
  id: props.weeklyMeditations?.previousWeek?.id || 0,
  title: props.weeklyMeditations?.previousWeek?.title || '',
  video_url: props.weeklyMeditations?.previousWeek?.video_url || '',
  date: props.weeklyMeditations?.previousWeek?.date || '',
  is_current: props.weeklyMeditations?.previousWeek?.is_current || false,
  created_at: props.weeklyMeditations?.previousWeek?.created_at || '',
});

const currentWeek = ref({
  id: props.weeklyMeditations?.currentWeek?.id || 0,
  title: props.weeklyMeditations?.currentWeek?.title || '',
  video_url: props.weeklyMeditations?.currentWeek?.video_url || '',
  date: props.weeklyMeditations?.currentWeek?.date || '',
  is_current: props.weeklyMeditations?.currentWeek?.is_current || false,
  created_at: props.weeklyMeditations?.currentWeek?.created_at || '',
});

const handleClose = () => {
  modalStore.close();
};

const validateForm = () => {
  const requiredFields: (string | number)[] = [
    previousWeek.value.title,
    previousWeek.value.video_url,
    currentWeek.value.title,
    currentWeek.value.video_url,
  ];

  if (props.mode === 'edit') {
    requiredFields.push(previousWeek.value.id, currentWeek.value.id);
  }

  if (requiredFields.some(field => !field)) {
    alert('데이터가 입력되지 않았습니다.');
    return false;
  }
  return true;
};

const handleSubmit = () => {
  if (!validateForm()) return;

  isSubmitting.value = true;

  props.handleEditMeditationClick({
    mode: props.mode,
    lastWeekData: {
      id: previousWeek.value.id,
      title: previousWeek.value.title,
      video_url: previousWeek.value.video_url,
    },
    currentWeekData: {
      id: currentWeek.value.id,
      title: currentWeek.value.title,
      video_url: currentWeek.value.video_url,
    },
  });

  setTimeout(() => {
    isSubmitting.value = false;
    handleClose();
  }, 300);
};
</script>

<template>
  <BaseModal
    :title="props.mode === 'create' ? '명상 등록하기' : '명상 수정하기'"
    width="360px"
    wrapper-class="gap-3"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4 mt-6 mb-8">
      <InputRow :max-length="100" v-model="previousWeek.title" label="지난 차수 명상 제목" />
      <InputRow :max-length="100" v-model="previousWeek.video_url" label="지난 차수 명상 링크" />
      <InputRow :max-length="100" v-model="currentWeek.title" label="이번 차수 명상 제목" />
      <InputRow :max-length="100" v-model="currentWeek.video_url" label="이번 차수 명상 링크" />
    </div>
    <template #footer>
      <ModalFooterConfirmButtons
        cancel-text="취소"
        :confirm-text="props.mode === 'create' ? '등록하기' : '수정하기'"
        :is-loading="isSubmitting"
        :disabled="!previousWeek.title || !previousWeek.video_url || !currentWeek.title || !currentWeek.video_url"
        @cancel="handleClose"
        @confirm="handleSubmit"
      />
    </template>
  </BaseModal>
</template>

<style scoped>
.plus-icon {
  font-size: 1.5rem;
  font-weight: bold;
  line-height: 1;
}
</style>
